/**
 * @class UsMap class for initializing the interactive US map that has links
 * @param {JSON} statesJSON Use Laravel states() helper: var states = '{!! states() !!}';
 * @param {object} settings Settings passed in as { description, urlPrefix, mapDivId, width }
 * @param {object} [stateSettings] State-specific settings
 * @returns void
 * Do not call this class directly, instead use the initMap function
 * Overall color of map is controlled by CSS variables,
 * see initMap function description for more detail
 */
export class UsMap {
    constructor(statesJSON, settings, stateSettings = {}) {
        this.urlRegex = new RegExp('^[a-z_-]*$');
        this.states = JSON.parse(statesJSON);
        this.description = settings.description;
        this.urlPrefix = settings.urlPrefix;
        this.targetDivId = settings.mapDivId;
        this.width = settings.width;
        this.stateSettings = stateSettings;
        if (this.validateSettings()) {
            this.init();
        }
    }

    makeStateData(stateData) {
        return Object.assign(
            {},
            {
                name: stateData.name,
                description: `Find ${this.description} in ${stateData.name}.`,
                color: 'default',
                hover_color: 'default',
                url: `/${this.urlPrefix}-in-${stateData.url_slug}`,
            },
            this.stateSettings[stateData.abbreviation],
        );
    }

    getAllStatesData() {
        const formattedStates = {};
        this.states.forEach( (state) => {
            formattedStates[state.abbreviation] = this.makeStateData(state);
        });
        return formattedStates;
    }

    validateSettings() {
        if (!this.urlRegex.test(this.urlPrefix)) {
            throw 'urlPrefix can only contain alphabetic characters, underscores, and/or hyphens';
        }
        if (!$(`#${this.targetDivId}`).length) {
            throw `Unable to find map target element in DOM, looking for element with ID of "${this.targetDivId}"`;
        }
        if (!this.states.length) {
            throw 'Please pass in states for the map to use';
        }
        if (this.checkEmpty(this.description) || this.checkEmpty(this.width)) {
            throw 'Please pass in a value for \'description\' and \'width\'';
        }
        return true;
    }

    checkEmpty(param) {
        return !param || param.toString().trim() === '';
    }

    init() {
        // This seems like we might be able to clean it up a bit?
        window.simplemaps_usmap_mapdata = {
            main_settings: {
                //General settings
                width: this.width,
                background_color: '#E7E7E7',
                background_transparent: 'yes',
                label_color: '#ffffff',
                border_color: '#E7E7E7',
                pop_ups: 'detect', //on_click, on_hover, or detect

                //State defaults
                state_description: '',
                state_color: '#2f9ede',
                state_hover_color: '#2191d2',
                state_url: '',
                all_states_inactive: 'no',

                //Location defaults
                location_description: '',
                location_color: '#8c1300',
                location_opacity: 0.8,
                location_url: '',
                location_size: 25,
                location_type: 'circle', //square or circle
                all_locations_inactive: 'yes',

                //Advanced settings - safe to ignore these
                url_new_tab: 'no',
                div: this.targetDivId,
                arrow_color: '#3B729F',
                arrow_color_border: '#8C1300',
                border_size: 1.5,
                popup_color: 'white',
                popup_opacity: 0.9,
                popup_shadow: 1,
                popup_corners: 5,
                popup_font: '12px/1.5 Arial, Helvetica, sans-serif',
                popup_nocss: 'no',
                initial_zoom: -1, //-1 is zoomed out, 0 is for the first continent etc
                initial_zoom_solo: 'no',
                all_states_zoomable: 'no',
                auto_load: 'yes',
                zoom: 'yes',
                js_hooks: 'no',
                hide_labels: 'no',
            },
            state_specific: this.getAllStatesData(),
        };
    }
}

/**
 * @function initMap Use this function to initialize a map, all params are required
 * @param {string} mapDivId ID of DOM element to put the map in
 * @param {JSON} statesJSON Use Laravel/our states() helper to set this: var states = '{!! states() !!}';
 * @param {string} description Used for hover text, renders as "Find {description} in Florida".
 * @param {string} urlPrefix Used for URL when user clicks state, renders as "/{urlPrefix}-in-florida"
 * @param {integer} width Width of map in pixels (generally 600-800)
 * @param {object} [stateSettings] State-specific settings
 * @returns void
 *
 * !!! NOTE !!! Map colors are controlled by CSS so we can use our brand variables
 * To use these colors, please put class="branded-us-map" on the HTML element you are rendering the map in
 */
export function initMap(mapDivId, statesJSON, description, urlPrefix, width, stateSettings = {}) {
    new UsMap(statesJSON, {description, urlPrefix, mapDivId, width}, stateSettings);
}
